import { useState, useCallback } from 'react';

const NONE = {};

/**
 * Inline form validation that never blocks the submit button.
 *
 * `compute()` returns `{ fieldName: 'message' }` for the CURRENT form values (empty object
 * = valid). Errors stay hidden until the user first presses submit; after that they are
 * live, so each message disappears as soon as the field is fixed.
 *
 *   const { errors, validate, reset } = useFormErrors(() => { ... return errs; });
 *   const handleSave = () => { if (!validate()) return; ...save... };
 *
 * Call `reset()` whenever the form is (re)opened or cleared.
 */
export function useFormErrors(compute) {
  const [attempted, setAttempted] = useState(false);
  const errors = attempted ? compute() : NONE;

  const validate = useCallback(() => {
    setAttempted(true);
    const ok = Object.keys(compute()).length === 0;
    if (!ok && typeof document !== 'undefined') {
      // Wait for the messages to render, then bring the first one into view.
      setTimeout(() => {
        const el = document.querySelector('[data-field-error]');
        el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      }, 30);
    }
    return ok;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compute]);

  const reset = useCallback(() => setAttempted(false), []);

  return { errors, validate, reset };
}
